// THE FREE CHECK ON THE PURCHASE PAGE: WHEN THE WEBSITE ASKS, HOW LONG IT WAITS, AND WHAT IT KEEPS
// (R-pending-web-free-first; Tyler, 2026-10-14: "A", and "They put their address in and when it takes them to the purchase
// or use credit page, it gracefully displays the free stuff", then "Yes on layout").
//
// THE SERVER DECIDES EVERYTHING ABOUT THE FREE CHECK: whether it runs, which county is asked, every sentence, and which of
// them the band shows first (`firstLook`, functions/src/freecheck/firstLook.ts). This module decides only when to ask and
// what to do with no answer:
//   - ASK ONCE PER APPEAL, only while the server says its switch is on (`capabilities.freeCheckFirstLook`), and never
//     wait longer than 45 seconds: a county's records can be slow, but the purchase page never waits on them.
//   - ANY failure — a refusal, an outage, a throw, the deadline — is NO ANSWER, and the page draws no band: the free check
//     never stands between an owner and the purchase (block vs degrade: nothing is withheld that the owner paid for).
//   - HELD IN THIS TAB'S MEMORY, per appeal, as a promise — so the band and a second visit to the page share one request —
//     and NOWHERE ELSE: no sessionStorage, no localStorage, no cookie. The published Privacy Policy (v10) says the tab keeps
//     only the appeal id and the signature, and this keeps nothing new. It dies with a reload, after which the page asks
//     again — the same call, by the same appeal id, which is all the tab still holds after Stripe's return.

import type { ResolveFreeCheckResponse } from "@contract";
import { FREE_FIRST_LINK, FREE_FIRST_LINK_COPY } from "../components/heldCopy";
import type { Transport } from "../transport/types";
import { within } from "./purchaseNotice";

/** The longest the purchase page waits for the free check: past this, no band. */
export const FREE_FIRST_DEADLINE_MS = 45_000;

export type FreeCheckAnswer = ResolveFreeCheckResponse["body"];

const held = new Map<string, Promise<FreeCheckAnswer | null>>();

/**
 * The server's free check for the appeal's home, asked at most once per appeal in this tab, or null: any failure, a throw
 * or no answer within `deadlineMs`. A transport that THROWS rather than answering is read as a failure too: the call is
 * made inside a promise, so a synchronous throw becomes a rejection, which `within` reads as no answer.
 */
export function askFreeCheckOnce(
  transport: Transport, appealId: string, deadlineMs: number = FREE_FIRST_DEADLINE_MS,
): Promise<FreeCheckAnswer | null> {
  const asked = held.get(appealId);
  if (asked !== undefined) return asked;
  const answer = within(Promise.resolve().then(() => transport.v2ResolveProperty.freeCheck({ appealId })), deadlineMs)
    .then((result) => (result !== null && result.ok ? result.response.body : null));
  held.set(appealId, answer);
  return answer;
}

/** Test-only, and a fresh tab (a reload): nothing held. */
export function forgetFreeChecks(): void {
  held.clear();
}

/** The band's link words: the one sentence Tyler approved (2026-10-15, `heldCopy.ts` `FREE_FIRST_LINK_COPY`), and its id,
 *  which the element carries as `data-held-copy` like every sentence this client holds. */
export function freeFirstLinkWords(): { id: string; text: string } {
  return { id: FREE_FIRST_LINK, text: FREE_FIRST_LINK_COPY[FREE_FIRST_LINK].text };
}
